import React, { useRef, useEffect, useState } from 'react';

/**
 * MobileTimeWheelPicker - iOS-style dual wheel picker for hours and minutes
 *
 * Props:
 * - value: { hours: number, minutes: number } - Current selected value
 * - onChange: (value) => void - Callback when value changes
 * - type: 'time' | 'duration' - Type of picker (affects available options)
 * - label: string - Label to display above picker
 */
const MobileTimeWheelPicker = ({ value, onChange, type = 'time', label }) => {
  const hoursRef = useRef(null);
  const minutesRef = useRef(null);
  const [isScrolling, setIsScrolling] = useState(false);

  // Configuration based on picker type
  const config = type === 'time'
    ? {
        hours: Array.from({ length: 24 }, (_, i) => i), // 0-23
        minutes: [0, 15, 30, 45]
      }
    : {
        hours: Array.from({ length: 13 }, (_, i) => i), // Duration: 0-12 hours
        minutes: [0, 30] // Only 00 and 30 minutes
      };

  const ITEM_HEIGHT = 40; // Height of each item in pixels

  // Format number to 2 digits
  const format = (num) => String(num).padStart(2, '0');

  // Scroll to centered position
  const scrollToValue = (containerRef, values, targetValue) => {
    if (!containerRef.current) return;

    const index = values.indexOf(targetValue);
    if (index === -1) return;

    const scrollPosition = index * ITEM_HEIGHT;
    containerRef.current.scrollTop = scrollPosition;
  };

  // Handle scroll end and snap to nearest value
  useEffect(() => {
    let scrollTimeout;

    const handleScroll = (ref, values, key) => {
      if (!ref.current) return;

      setIsScrolling(true);
      clearTimeout(scrollTimeout);

      scrollTimeout = setTimeout(() => {
        const scrollTop = ref.current.scrollTop;
        const index = Math.round(scrollTop / ITEM_HEIGHT);
        const clampedIndex = Math.max(0, Math.min(values.length - 1, index));
        const newValue = values[clampedIndex];

        // Snap to position with smooth scroll
        scrollToValue(ref, values, newValue);

        // Update value
        if (value[key] !== newValue) {
          onChange({ ...value, [key]: newValue });
        }

        setIsScrolling(false);
      }, 100); // Reduced from 150ms to 100ms for snappier feel
    };

    const hoursEl = hoursRef.current;
    const minutesEl = minutesRef.current;

    const handleHoursScroll = () => handleScroll(hoursRef, config.hours, 'hours');
    const handleMinutesScroll = () => handleScroll(minutesRef, config.minutes, 'minutes');

    if (hoursEl) hoursEl.addEventListener('scroll', handleHoursScroll);
    if (minutesEl) minutesEl.addEventListener('scroll', handleMinutesScroll);

    return () => {
      if (hoursEl) hoursEl.removeEventListener('scroll', handleHoursScroll);
      if (minutesEl) minutesEl.removeEventListener('scroll', handleMinutesScroll);
      clearTimeout(scrollTimeout);
    };
  }, [value, config.hours, config.minutes, onChange]);

  // Initialize scroll positions
  useEffect(() => {
    scrollToValue(hoursRef, config.hours, value.hours);
    scrollToValue(minutesRef, config.minutes, value.minutes);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  // Render wheel items
  const renderWheel = (values, selectedValue, ref) => (
    <div className="relative h-[200px] overflow-hidden bg-white dark:bg-gray-800 rounded-lg">
      {/* Gradient overlays - placed first so they're behind content */}
      <div className="absolute top-0 left-0 right-0 h-20 bg-gradient-to-b from-white dark:from-gray-800 to-transparent pointer-events-none z-20" />
      <div className="absolute bottom-0 left-0 right-0 h-20 bg-gradient-to-t from-white dark:from-gray-800 to-transparent pointer-events-none z-20" />

      {/* Selection highlight - semi-transparent so text shows through */}
      <div className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-[40px] bg-blue-100/50 dark:bg-blue-900/20 border-y-2 border-blue-500 dark:border-blue-600 pointer-events-none z-10" />

      {/* Scrollable container */}
      <div
        ref={ref}
        className="h-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide relative z-0"
        style={{
          scrollBehavior: 'smooth',
          paddingTop: '80px', // Center first item
          paddingBottom: '80px', // Center last item
          WebkitOverflowScrolling: 'touch' // Enable momentum scrolling on iOS
        }}
      >
        {values.map((val) => {
          const isSelected = val === selectedValue;
          return (
            <div
              key={val}
              className={`
                h-[40px] flex items-center justify-center snap-center
                transition-all duration-200
                ${isSelected
                  ? 'text-2xl font-bold text-gray-900 dark:text-white scale-110 relative z-30'
                  : 'text-lg text-gray-400 dark:text-gray-500'
                }
              `}
              style={{ lineHeight: '40px' }}
            >
              {format(val)}
            </div>
          );
        })}
      </div>
    </div>
  );

  return (
    <div className="w-full">
      {label && (
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          {label}
        </label>
      )}

      <div className="flex items-center justify-center gap-2 bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md">
        {/* Hours wheel */}
        <div className="flex-1 max-w-[80px]">
          <div className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">
            {type === 'duration' ? 'Hrs' : 'Hour'}
          </div>
          {renderWheel(config.hours, value.hours, hoursRef)}
        </div>

        {/* Colon separator */}
        <div className="text-3xl font-bold text-gray-900 dark:text-white mt-6">
          :
        </div>

        {/* Minutes wheel */}
        <div className="flex-1 max-w-[80px]">
          <div className="text-xs text-center text-gray-500 dark:text-gray-400 mb-2">
            Min
          </div>
          {renderWheel(config.minutes, value.minutes, minutesRef)}
        </div>
      </div>
    </div>
  );
};

export default MobileTimeWheelPicker;
